import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProgereBrand } from "@/components/ProgereBrand";
import { Crown, ExternalLink, ArrowLeft, Ticket, AlertTriangle } from "lucide-react";

const card = "rounded-xl border border-white/10 bg-[#111217] p-5";

export default function MasterCenterPage() {
  const { module } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [centers, setCenters] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    if (module) {
      api.get(`/platform/master/${module}`)
        .then(({ data }) => setData(data))
        .catch(() => { toast.error("Accès refusé ou module introuvable"); navigate("/app/master"); })
        .finally(() => setLoading(false));
    } else {
      api.get("/platform/master-centers")
        .then(({ data }) => setCenters(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [module, navigate]);

  if (user && !user.is_platform_owner) return <Navigate to="/app" replace />;

  const Stat = ({ label, value, accent }) => (
    <div className={card}><div className="text-xs uppercase tracking-wider text-white/45">{label}</div>
      <div className={`mt-1 text-3xl font-black ${accent ? "text-[#EAB308]" : "text-white"}`}>{value ?? "—"}</div></div>
  );

  // --- Vue module (MASTER d'un module) ---
  if (module) {
    return (
      <div className="space-y-6" data-testid="master-module-page">
        <button onClick={() => navigate("/app/master")} className="flex items-center gap-2 text-sm text-white/50 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Master Centers
        </button>
        {loading ? <p className="text-white/40">Chargement…</p> : data && (
          <>
            <div className="rounded-2xl border border-[#EAB308]/25 bg-gradient-to-b from-[#15161b] to-[#0e0f13] p-6" data-testid="master-identity">
              <div className="flex items-center gap-2 text-[#EAB308] text-xs font-bold uppercase tracking-widest mb-2">
                <Crown className="w-4 h-4" /> Master Center
              </div>
              <h1 className="text-3xl font-black">
                <ProgereBrand module={data.name.replace(/^PROGÈRE\s*/i, "")} size="large" />
              </h1>
              <div className="mt-2 flex items-center gap-3 text-xs text-white/50">
                <span className="rounded px-2 py-0.5 font-mono text-[#EAB308] bg-[#EAB308]/10">v{data.version}</span>
                <span className="flex items-center gap-1 text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" /> {data.status}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Stat label="Billets ouverts" value={data.counts.open_tickets} accent />
              <Stat label="Billets urgents" value={data.counts.urgent} />
              <Stat label="Total billets" value={data.counts.total_tickets} />
            </div>

            <div className={card}>
              <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-white"><Ticket className="w-5 h-5 text-[#EAB308]" /> Billets ouverts</h2>
              <div className="space-y-2">
                {data.open_tickets.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-lg border border-white/8 bg-black/30 px-4 py-2.5" data-testid={`master-ticket-${t.ref}`}>
                    <div>
                      <div className="text-sm font-semibold text-white">{t.subject}</div>
                      <div className="text-xs font-mono text-[#EAB308]">{t.ref} · {t.priority}</div>
                    </div>
                    <span className="rounded px-2 py-0.5 text-[11px] text-white/70 bg-white/8">{t.status}</span>
                  </div>
                ))}
                {data.open_tickets.length === 0 && <p className="text-sm text-white/40">Aucun billet ouvert pour ce module.</p>}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // --- Vue switcher (/app/master) ---
  return (
    <div className="space-y-6" data-testid="master-switcher-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10"><Crown className="w-5 h-5 text-[#EAB308]" /></div>
        <div>
          <h1 className="text-2xl font-black"><ProgereBrand module="MASTER CENTERS" size="medium" /></h1>
          <p className="text-sm text-white/50">Sélectionnez un centre à ouvrir</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {centers.map((m) => (
          <div key={m.code} className={card} data-testid={`master-switch-${m.code}`}>
            <div className="flex items-center justify-between">
              <div className="text-lg font-black text-white uppercase">{m.name.replace(/^PROGÈRE\s*/i, "")}</div>
              <span className="rounded px-2 py-0.5 text-[11px] font-mono text-[#EAB308] bg-[#EAB308]/10">v{m.version}</span>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-white/50">
              <span className="flex items-center gap-1"><Ticket className="w-3.5 h-3.5" /> {m.open_tickets}</span>
              <span className="flex items-center gap-1 text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-400" /> {m.status}</span>
            </div>
            <button onClick={() => navigate(`/app/master/${m.code}`)} data-testid={`open-master-${m.code}`}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-[#EAB308]/40 bg-[#EAB308]/10 px-4 py-2 text-sm font-semibold text-[#EAB308] hover:bg-[#EAB308]/20">
              <ExternalLink className="w-4 h-4" /> Ouvrir le MASTER
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
